import { useCallback, useEffect, useState } from "react";

import { purchaseOrders } from "~/data/mockData";
import { supabase } from "~/integrations/supabase/client";
import { useProfileStore } from "~/store/profileStore";
import { Client } from "~/types/client";
import { PurchaseOrder } from "~/types/user";

export const useClientDetail = (clientId: string | undefined) => {
  const [client, setClient] = useState<Client | null>(null);
  const [clientOrders, setClientOrders] = useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const { role } = useProfileStore();
  const isSeller = role === "seller";

  const fetchClientDetails = useCallback(async () => {
    if (!clientId) {
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      // Fetch client data from Supabase with proper column specification
      let query = supabase
        .from("clients")
        .select(
          `
          *, 
          profiles:assigned_seller(id, name, lastname),
          sales_channels:sales_channel(id, name),
          auxiliary_types:auxiliary_type(id, name)
        `
        )
        .eq("id", clientId);

      // Only exclude deleted clients for sellers
      if (isSeller) {
        query = query.is("deleted_at", null);
      }
      // For admin/superadmin, include deleted clients

      const { data: clientData, error: clientError } = await query.single();

      if (clientError) throw clientError;

      // Transform data to Client type
      if (clientData) {
        // Use type assertion to handle Supabase's typing issues
        const sellerProfile = clientData.profiles as any;
        const sellerName = sellerProfile
          ? `${sellerProfile.name || ""} ${sellerProfile.lastname || ""}`.trim()
          : undefined;

        setClient({
          id: clientData.id,
          name: clientData.name,
          company: clientData.name, // Using name as company for now
          email: clientData.email || "",
          phone: clientData.phone_number || "",
          activity: clientData.activity || "",
          creditScore: 5, // Mock data
          riskScore: 3, // Mock data
          paymentHistory: "good", // Mock data
          isActive: clientData.is_active ?? true,
          is_active: clientData.is_active ?? true,
          deactivation_reason: clientData.deactivation_reason || null,
          sellerName,
          assigned_seller: clientData.assigned_seller,
          taxId: clientData.tax_id,
          tax_id: clientData.tax_id, // Add both for compatibility
          insuredAmount: clientData.insured_amount || 0,
          trailingPaymentDays: clientData.trailing_payment_days || 0,
          risk_checked: clientData.risk_checked || false,
          salesConditionId: clientData.sales_condition_id || "",
          internal_notes: clientData.internal_notes || "",
          contact_person: clientData.contact_person || "",
          deleted_at: clientData.deleted_at || null,
          // Add the new foreign key fields
          collection_contact_person: clientData.collection_contact_person || "",
          collection_contact_email: clientData.collection_contact_email || "",
          collection_contact_phone: clientData.collection_contact_phone || "",
          sales_channel: clientData.sales_channel || "",
          auxiliary_type: clientData.auxiliary_type || "",
          sales_channel_name: (clientData as any).sales_channels?.name || "",
          auxiliary_type_name: (clientData as any).auxiliary_types?.name || "",
          zone: clientData.zone || "",
          siniestro: clientData.siniestro || false,
          cobranza_judicial: clientData.cobranza_judicial || false,
        } as any);
      }

      // For now, still using mock orders data
      const clientPurchaseOrders = purchaseOrders.filter(
        (order) => String(order.clientId) === String(clientId)
      );
      setClientOrders(clientPurchaseOrders);
    } catch (error) {
      console.error("Error fetching client details:", error);
      // If not found, try to use mock data as fallback for demo purposes
      const fallbackOrders = purchaseOrders.filter(
        (order) => String(order.clientId) === String(clientId)
      );
      setClientOrders(fallbackOrders);
    } finally {
      setLoading(false);
    }
  }, [clientId, isSeller]);

  // Initial fetch
  useEffect(() => {
    fetchClientDetails();
  }, [fetchClientDetails]);

  return {
    client,
    clientOrders,
    loading,
    refreshClient: fetchClientDetails, // Expose the refresh function
  };
};
